import { useState, useMemo } from 'react';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart, Line } from 'recharts';
import { TrendingUp, TrendingDown, Calendar } from 'lucide-react';
import { Transaction } from '../../types';

interface IncomeExpenseChartProps {
  transactions: Transaction[];
}

interface MonthlyData {
  month: string;
  income: number;
  expenses: number;
  net: number;
}

export default function IncomeExpenseChart({ transactions }: IncomeExpenseChartProps) {
  const [timeRange, setTimeRange] = useState<'month' | 'quarter' | 'year'>('month');

  // Process data for the last 6 months
  const monthlyData = useMemo(() => {
    const dataMap = new Map<string, { income: number; expenses: number }>();
    
    // Get transactions from the last 6 months
    transactions.forEach(transaction => {
      const date = new Date(transaction.date);
      const monthKey = date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
      
      const current = dataMap.get(monthKey) || { income: 0, expenses: 0 };
      
      if (transaction.type === 'income') {
        current.income += transaction.amount;
      } else {
        current.expenses += Math.abs(transaction.amount);
      }
      
      dataMap.set(monthKey, current);
    });
    
    // Generate last 6 months
    const result: MonthlyData[] = [];
    for (let i = 5; i >= 0; i--) {
      const date = new Date();
      date.setMonth(date.getMonth() - i);
      const monthKey = date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
      
      const values = dataMap.get(monthKey) || { income: 0, expenses: 0 };
      result.push({
        month: monthKey,
        income: values.income,
        expenses: values.expenses,
        net: values.income - values.expenses
      });
    }
    
    return result;
  }, [transactions]);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-3 shadow-lg">
          <p className="text-sm font-medium text-gray-300 mb-2">{label}</p>
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                <span className="text-sm text-gray-400">Income:</span>
              </div>
              <span className="font-medium text-green-400">
                ${payload.find((p: any) => p.dataKey === 'income')?.value.toLocaleString()}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-2 h-2 bg-red-500 rounded-full mr-2"></div>
                <span className="text-sm text-gray-400">Expenses:</span>
              </div>
              <span className="font-medium text-red-400">
                ${payload.find((p: any) => p.dataKey === 'expenses')?.value.toLocaleString()}
              </span>
            </div>
            <div className="flex items-center justify-between pt-1 border-t border-gray-700">
              <span className="text-sm text-gray-400">Net:</span>
              <span className={`font-medium ${payload.find((p: any) => p.dataKey === 'net')?.value >= 0 ? 'text-blue-400' : 'text-orange-400'}`}>
                ${payload.find((p: any) => p.dataKey === 'net')?.value.toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  const totalIncome = monthlyData.reduce((sum, month) => sum + month.income, 0);
  const totalExpenses = monthlyData.reduce((sum, month) => sum + month.expenses, 0);
  const netTotal = totalIncome - totalExpenses;

  return (
    <div className="chart-container">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-4">
        <div className="mb-3 lg:mb-0">
          <h3 className="text-lg font-semibold text-white">Income vs Expenses</h3>
          <p className="text-sm text-gray-400">6-month trend analysis</p>
        </div>
        
        <div className="flex items-center space-x-1 sm:space-x-2">
          {(['month', 'quarter', 'year'] as const).map((range) => (
            <button
              key={range}
              onClick={() => setTimeRange(range)}
              className={`px-2 sm:px-3 py-1 text-xs sm:text-sm rounded-lg transition-colors ${
                timeRange === range
                  ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                  : 'text-gray-500 hover:text-gray-300 hover:bg-gray-700/50'
              }`}
            >
              {range.charAt(0).toUpperCase() + range.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3 mb-6">
        <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3">
          <div className="flex items-center">
            <TrendingUp className="w-4 h-4 text-green-400 mr-2" />
            <span className="text-sm text-gray-400">Total Income</span>
          </div>
          <p className="text-lg sm:text-xl font-bold text-green-400 mt-1">${totalIncome.toLocaleString()}</p>
        </div>
        
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
          <div className="flex items-center">
            <TrendingDown className="w-4 h-4 text-red-400 mr-2" />
            <span className="text-sm text-gray-400">Total Expenses</span>
          </div>
          <p className="text-lg sm:text-xl font-bold text-red-400 mt-1">${totalExpenses.toLocaleString()}</p>
        </div>
        
        <div className={`rounded-lg p-3 ${netTotal >= 0 ? 'bg-blue-500/10 border-blue-500/20' : 'bg-orange-500/10 border-orange-500/20'}`}>
          <div className="flex items-center">
            <Calendar className={`w-4 h-4 mr-2 ${netTotal >= 0 ? 'text-blue-400' : 'text-orange-400'}`} />
            <span className={`text-sm ${netTotal >= 0 ? 'text-gray-400' : 'text-gray-400'}`}>Net Total</span>
          </div>
          <p className={`text-lg sm:text-xl font-bold mt-1 ${netTotal >= 0 ? 'text-blue-400' : 'text-orange-400'}`}>
            ${netTotal.toLocaleString()}
          </p>
        </div>
      </div>

      {/* Chart */}
      <div className="h-48 sm:h-56 lg:h-64">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={monthlyData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={false} />
            <XAxis 
              dataKey="month" 
              stroke="#9CA3AF"
              fontSize={12}
              tick={{ fill: '#9CA3AF' }}
            />
            <YAxis 
              stroke="#9CA3AF"
              fontSize={12}
              tick={{ fill: '#9CA3AF' }}
              tickFormatter={(value) => `$${value}`}
            />
            <Tooltip content={<CustomTooltip />} />
            <defs>
              <linearGradient id="colorIncome" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
              </linearGradient>
              <linearGradient id="colorExpenses" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#ef4444" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <Area 
              type="monotone" 
              dataKey="income" 
              stroke="#10b981" 
              fill="url(#colorIncome)"
              strokeWidth={2}
              name="Income"
            />
            <Area 
              type="monotone" 
              dataKey="expenses" 
              stroke="#ef4444" 
              fill="url(#colorExpenses)"
              strokeWidth={2}
              name="Expenses"
            />
            <Line 
              type="monotone" 
              dataKey="net" 
              stroke="#3b82f6" 
              strokeWidth={1.5}
              strokeDasharray="3 3"
              dot={{ r: 2 }}
              name="Net"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 mt-4 text-sm">
        <div className="flex items-center">
          <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
          <span className="text-gray-400">Income</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 bg-red-500 rounded-full mr-2"></div>
          <span className="text-gray-400">Expenses</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
          <span className="text-gray-400">Net</span>
        </div>
      </div>
    </div>
  );
}